// eslint-disable-next-line import/named
import { customElement } from 'lit/decorators.js';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import {
  DataTracker,
  DynamicValueBinder,
  FieldType
} from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { EventTemplate, Snippet } from '../../../webmodule-common/other/ui/events';
import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { html } from 'lit';
import { isAutoSaving } from '../../../webmodule-common/other/save-workflow';
import { LangFranchiseeStockView } from '../../../localization/scripts/franchisee/view/franchisee-stock-view-lang';
import { DataTableWrapper } from '../../../webmodule-common/other/ui/datatable-view';
import { PageControlTabWithIndependantSaving } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { RequestPage, ResultPaginated } from '../../../webmodule-common/other/ui/RequestPage';
import { StockItem } from '../../api/dealer-api-interface-franchisee';
import { StockManager } from '../data/stock-manager';
import { supplierItemContentTypeDisplay } from '../../quotes/data/supplier-quote-item-content-type';
import {
  WebmoduleBlurEvent,
  WebmoduleInput,
  WebmoduleInputMoney,
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../webmodule-common/components/src/webmodule-components';

interface FranchiseeStockOptions {
  stockManager: StockManager;
}

interface FranchiseeStockTableOptions extends FranchiseeStockOptions {
  formInputAssistant?: FormInputAssistant;
}

@customElement('wm-franchiseestocktable')
export class FranchiseeStockTable extends DataTableWrapper<StockItem> {
  stockManager: StockManager;
  formInputAssistant?: FormInputAssistant;

  filter?: string;

  constructor(options?: FranchiseeStockTableOptions) {
    super();

    const resolveOptions: FranchiseeStockTableOptions = {
      stockManager: new StockManager(null),
      formInputAssistant: undefined,
      ...options
    };

    this.stockManager = resolveOptions.stockManager;
    this.formInputAssistant = resolveOptions.formInputAssistant;
  }

  getDefaultSortAsc(): boolean {
    return false;
  }

  enableFiltering(): boolean {
    return true;
  }

  updateFilter(_searchTerm: string | null) {
    if (_searchTerm) this.filter = _searchTerm;
    else {
      this.filter = undefined;
      this.stockManager.refreshItemsFlag = true;
    }
  }

  override async getRowsFromServer(_request: RequestPage): Promise<ResultPaginated<StockItem>> {
    await this.stockManager.needsStockItems(this.filter);

    const items = this.stockManager.items;

    if (!items)
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: 2000,
        results: []
      };

    // Order by content type so each category's items are contiguous; the table's
    // native grouping renders a category header band per run (see getGroupByKey).
    const results = [...items].sort((a, b) => {
      return a.itemContentType === b.itemContentType ? 0 : a.itemContentType > b.itemContentType ? 1 : -1;
    });

    return {
      count: results.length,
      pageCount: 1,
      pageIndex: 0,
      pageSize: results.length,
      results: results
    };
  }

  // Group rows by content type, using its display name as the group key so the
  // table renders a native category header band (replacing the old synthetic
  // subheader rows).
  override getGroupByKey(): (item: StockItem) => string | null {
    return (item: StockItem) => supplierItemContentTypeDisplay(item.itemContentType, true, true);
  }

  getColumns(): WebModuleTableColumnDef[] {
    const cols: WebModuleTableColumnDef[] = [];
    cols.push({
      title: 'Code',
      fieldName: 'code',
      minWidth: '200px',
      displayValue: (_table: WebModuleTable, row: StockItem) => {
        return row.code;
      }
    });

    cols.push({
      title: 'Description',
      fieldName: 'description',
      minWidth: '240px',
      displayValue: (_table: WebModuleTable, row: StockItem) => {
        const field = `stock_description_${row.id}`;
        return this.formInputAssistant ? this.formInputAssistant?.text1(field, undefined, undefined) : html``;
      }
    });

    cols.push({
      title: 'Cost Per Unit',
      fieldName: 'singleUnitCost',
      width: '200px',
      displayValue: (_table: WebModuleTable, row: StockItem) => {
        const field = `stock_unit_cost_${row.id}`;
        return this.formInputAssistant ? this.formInputAssistant?.int(field, undefined, undefined) : html``;
      }
    });

    return cols;
  }

  // The stock list is loaded in full (grouped by content type), so it is never
  // server-paged. Drives pageLength, showPaginationInfo and pageSizeOptions together,
  // keeping it on a single page with no paginator, summary or size selector.
  override disablePagination(): boolean {
    return true;
  }

  // Bound the table body to the space left under the settings tab chrome so the
  // table owns the only vertical scroll (sticky header) - instead of the default
  // 70vh cap, which left a second tab scrollbar. Viewport-relative, so no fragile
  // flex-chain through the view's form/heading wrapper is needed. The constant
  // covers the wm-tab-manager chrome (site header, tab strip with the nav
  // button bar, panel padding) with a few pixels of slack.
  override bodyMaxHeight(): string | undefined {
    return 'calc(100vh - 270px)';
  }
}

@customElement('wm-franchiseestockview')
export class FranchiseeStockView extends PageControlTabWithIndependantSaving {
  public ShowTab = false;
  protected table: FranchiseeStockTable;
  private stockManager: StockManager;
  private readonly dataBinding: DataBinding;
  private readonly dataTracker: DataTracker;

  constructor(options?: FranchiseeStockOptions) {
    super();
    this.pageFragment = 'stock';
    const resolveOptions: Required<FranchiseeStockOptions> = {
      stockManager: new StockManager(null),
      ...options
    };

    this.stockManager = resolveOptions.stockManager;

    this.dataBinding = new DataBinding(this.ui, this.elementId, input => {
      return `${input}`;
    });

    this.dataBinding.allowMissingElements = true;
    this.dataTracker = new DataTracker(this.dataBinding);

    this.table = this.stockTableFactory({
      formInputAssistant: new FormInputAssistant(this.dataTracker),
      ...resolveOptions
    });
  }

  override async afterConstruction(): Promise<void> {
    await this.stockManager.needsStockItems();

    this.ShowTab = this.stockManager.showTab;

    if (this.stockManager.items) {
      for (const item of this.stockManager.items) {
        this.addStockBinding(item.id);
      }
    }
  }

  getValidationErrors(): string[] {
    const errors: string[] = [];

    if (!this.stockManager.reportValidation()) {
      this.stockManager.getValidation().map(x => errors.push(`Code (${x.code}) - ${x.message}`));
    }

    return errors;
  }

  public async prepareForSave(): Promise<void> {
    if (this.dataTracker.modified) this.dataTracker.applyChangeToValue();
  }

  public internalDataChanged(): boolean {
    return this.stockManager.changed();
  }

  async onEnter(): Promise<void> {
    this.requestUpdate();
    await this.table.refreshData();
  }

  public allowDeletePage(): boolean {
    return false;
  }

  protected async internalSaveData(): Promise<boolean> {
    console.log('Saving Franchisee Stock');

    const result = await this.stockManager.saveStockItems();

    if (!result) return false;

    if (!isAutoSaving()) fireQuickSuccessToast(LangFranchiseeStockView.stockSaved());

    return true;
  }

  protected getCaption(): Snippet {
    return LangFranchiseeStockView.stock();
  }

  protected bodyTemplate(): EventTemplate {
    // No in-content heading: the "Buy-In Items" settings tab label already names it,
    // and dropping it gives the table more height.
    return html` <div>
      <form id="stockForm" class="form-one-col">
        <div class="row" @webmodule-blur="${(e: WebmoduleBlurEvent) => this.editVal(e)}">${this.table}</div>
      </form>
    </div>`;
  }

  protected stockTableFactory(options?: FranchiseeStockTableOptions) {
    return new FranchiseeStockTable(options);
  }

  private editVal(e: WebmoduleBlurEvent) {
    const target = e.target as WebmoduleInput | WebmoduleInputMoney | undefined;

    if (target) this.dataTracker.setEditorValue(target.id, target.value);
  }

  // stock_description

  private addStockBinding(itemId: string) {
    const fieldSingleUnitCost = `stock_unit_cost_${itemId}`;
    const fielddescription = `stock_description_${itemId}`;

    this.dataTracker.addBinding(
      new DynamicValueBinder(
        FieldType.money,
        true,
        () => {
          const si = this.stockManager.getStockItem(itemId);

          if (!si) return null;

          return si.singleUnitCost == null ? null : (si.singleUnitCost as number);
        },
        value => {
          this.stockManager.updateStockItem(itemId, undefined, value as number);
        },
        () => false
      ),
      fieldSingleUnitCost,
      fieldSingleUnitCost,
      FieldType.money,
      true
    );

    this.dataTracker.addBinding(
      new DynamicValueBinder(
        FieldType.string,
        true,
        () => {
          const si = this.stockManager.getStockItem(itemId);

          if (!si) return null;

          return si.description == null ? null : (si.description as string);
        },
        value => {
          this.stockManager.updateStockItem(itemId, value as string, undefined);
        },
        () => false
      ),
      fielddescription,
      fielddescription,
      FieldType.string,
      false
    );
  }
}
